/**
 * PATH: packages/ui/src/shortcuts/overrides.ts
 * FILE_ID: UI-SHORTCUTS-OVERRIDES-001
 * PURPOSE: Pure folding of a user ShortcutOverrideMap onto action descriptors,
 *          plus conflict detection used by the Settings editor.
 * WHY: Overrides (remap / enable-disable) live in ui_preferences and must be
 *      applied identically by the dispatcher (what fires) and the editor (what
 *      shows). Keeping the fold pure makes both paths testable + consistent.
 *
 * FLOW:
 *   effectiveShortcut(action, override) → { binding, enabled }
 *   findConflicts(effective[])          → actionId → conflictingActionId[]
 *
 * RELATED:
 *   - ./types.ts: ShortcutAction, ShortcutOverride(Map), EffectiveShortcut
 *   - ./binding.ts: parseBinding (for normalized conflict keys)
 */

import type {
  EffectiveShortcut,
  ShortcutAction,
  ShortcutOverride,
  ShortcutOverrideMap,
} from './types'
import { parseBinding } from './binding'

/**
 * effectiveShortcut — resolve one action against its (optional) override.
 *   binding:  override.binding when the key is PRESENT (incl. null=unbound),
 *             else the action default.
 *   enabled:  false only when the user explicitly disabled it.
 * Pure.
 */
export function effectiveShortcut(
  action: ShortcutAction,
  override?: ShortcutOverride,
): EffectiveShortcut {
  const hasBindingOverride = override ? Object.prototype.hasOwnProperty.call(override, 'binding') : false
  const binding = hasBindingOverride ? (override!.binding ?? null) : action.defaultBinding
  const enabled = override?.enabled !== false
  return { action, binding, enabled }
}

/** applyOverrides — fold a whole override map onto a list of actions. Pure. */
export function applyOverrides(
  actions: ShortcutAction[],
  overrides: ShortcutOverrideMap = {},
): EffectiveShortcut[] {
  return actions.map((a) => effectiveShortcut(a, overrides[a.id]))
}

/**
 * True when an override entry carries no information beyond the default
 * (no binding key AND not explicitly disabled), so it can be dropped to keep
 * the persisted map minimal. Pure.
 */
function isRedundantOverride(entry: ShortcutOverride): boolean {
  const hasBinding = Object.prototype.hasOwnProperty.call(entry, 'binding')
  return !hasBinding && entry.enabled !== false
}

/**
 * mergeOverride — apply a patch to one action's override in an immutable map.
 * A patch field set to `undefined` clears that field (back to default). The
 * resulting entry is pruned when it becomes redundant (so resetting both
 * fields removes the key entirely). Pure.
 */
export function mergeOverride(
  map: ShortcutOverrideMap,
  id: string,
  patch: ShortcutOverride,
): ShortcutOverrideMap {
  const next: ShortcutOverrideMap = { ...map }
  const merged: ShortcutOverride = { ...(next[id] ?? {}) }

  if (Object.prototype.hasOwnProperty.call(patch, 'binding')) {
    if (patch.binding === undefined) delete merged.binding
    else merged.binding = patch.binding
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'enabled')) {
    if (patch.enabled === undefined) delete merged.enabled
    else merged.enabled = patch.enabled
  }

  if (isRedundantOverride(merged)) delete next[id]
  else next[id] = merged
  return next
}

/** clearOverride — remove one action's override (reset to default). Pure. */
export function clearOverride(map: ShortcutOverrideMap, id: string): ShortcutOverrideMap {
  if (!(id in map)) return map
  const next = { ...map }
  delete next[id]
  return next
}

/**
 * coerceOverrideMap — defensively normalize an untrusted value (localStorage
 * JSON or a backend response) into a clean ShortcutOverrideMap: drops non-object
 * entries, keeps only `binding` (string|null) and `enabled` (boolean), and
 * skips redundant entries. Mirrors the backend's _clean_shortcut_overrides so
 * the FE never trusts malformed persisted data. Pure.
 */
export function coerceOverrideMap(value: unknown): ShortcutOverrideMap {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {}
  const out: ShortcutOverrideMap = {}
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue
    const r = raw as Record<string, unknown>
    const entry: ShortcutOverride = {}
    if ('binding' in r) {
      if (r.binding === null) entry.binding = null
      else if (typeof r.binding === 'string') entry.binding = r.binding
    }
    if ('enabled' in r && typeof r.enabled === 'boolean') entry.enabled = r.enabled
    if (isRedundantOverride(entry)) continue
    out[id] = entry
  }
  return out
}

/** Stable scope key so identical chords only conflict within the same scope. */
function scopeKey(action: ShortcutAction): string {
  const s = action.scope
  if (s.kind === 'global') return 'global'
  if (s.kind === 'hub') return `hub:${s.hub}`
  return `screen:${s.screen}`
}

/** Normalize a binding to a canonical comparison key (modifier-order-stable). */
function bindingKey(binding: string): string {
  return parseBinding(binding)
    .map((st) => [st.mod && 'mod', st.ctrl && 'ctrl', st.alt && 'alt', st.shift && 'shift', st.key]
      .filter(Boolean)
      .join('+'))
    .join(' ')
}

/**
 * findConflicts — within each scope, group ENABLED, bound shortcuts by their
 * normalized binding; any group of 2+ is a conflict. Returns a map of every
 * conflicting actionId → the other actionIds it clashes with. Pure.
 *
 * Used by the customization editor to surface a "warn + allow" badge (the
 * chosen conflict policy); the dispatcher itself just picks by precedence.
 */
export function findConflicts(effective: EffectiveShortcut[]): Record<string, string[]> {
  const groups = new Map<string, string[]>()
  for (const e of effective) {
    if (!e.enabled || !e.binding) continue
    const key = `${scopeKey(e.action)}|${bindingKey(e.binding)}`
    const arr = groups.get(key) ?? []
    arr.push(e.action.id)
    groups.set(key, arr)
  }
  const conflicts: Record<string, string[]> = {}
  for (const ids of groups.values()) {
    if (ids.length < 2) continue
    for (const id of ids) {
      conflicts[id] = ids.filter((other) => other !== id)
    }
  }
  return conflicts
}
